import {
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import WhiteCard from "../WhiteCard";
import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "../Spinner";
import NewMessageForm from "./components/MessagesList/components/NewMessageForm";
import MessagesList from "./components/MessagesList";
import { useGetMessageThreadsByPatientIdQuery } from "src/api/services/messagesApi";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import { isNonProviderView } from "src/utils/isNonProviderView";

interface Props {
  patientId: number;
  onSuccess?: () => void;
  allowMessaging?: boolean;
}

const Messaging = ({ patientId, onSuccess, allowMessaging = true }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { data, isLoading } = useGetMessageThreadsByPatientIdQuery(patientId);
  const navigate = useNavigate();

  const isNPV = useMemo(() => {
    return isNonProviderView(pathname);
  }, [pathname]);

  const messageThreads = useMemo(() => {
    if (data == null) return [];
    return data;
  }, [data]);

  const selectedThread = useMemo(
    () => searchParams.get("thread") ?? messageThreads[0]?.slug,
    [messageThreads, searchParams],
  );

  const handleExpand = useCallback(() => {
    if (selectedThread == null) return;
    return navigate(`messages/${selectedThread}`);
  }, [navigate, selectedThread]);

  const handleSelectThread = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchParams({ thread: event.target.value });
    },
    [setSearchParams],
  );

  const theThread = useMemo(() => {
    return (
      messageThreads.find((t) => t.is_active === true)?.slug ||
      messageThreads[0]?.slug
    );
  }, [messageThreads]);

  const canSendMessage = useMemo(() => {
    const thread = messageThreads.find(
      (thread) => thread.slug === selectedThread,
    );

    if (thread == null || thread.is_active === false) return false;

    return allowMessaging;
  }, [allowMessaging, messageThreads, selectedThread]);

  const changeThread = useCallback(() => {
    if (messageThreads.length === 0) return false;

    if (searchParams.get("thread") == null) {
      searchParams.set("thread", theThread);
      setSearchParams(searchParams);
      return true;
    }

    if (
      messageThreads.find(
        (thread) => searchParams.get("thread") == thread.slug,
      ) == null
    ) {
      searchParams.set("thread", theThread);
      setSearchParams(searchParams);
      return true;
    }

    return false;
  }, [messageThreads, searchParams, setSearchParams, theThread]);

  useEffect(() => {
    changeThread();
  }, [changeThread]);

  return (
    <WhiteCard gap={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">MESSAGES</Typography>
        {isNPV ? (
          <Tooltip title="Expand">
            <IconButton onClick={handleExpand}>
              <OpenWithIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      {isLoading ? (
        <Spinner />
      ) : selectedThread ? (
        <Stack height="657px" sx={{ overflowY: "auto" }}>
          <TextField
            select
            value={selectedThread}
            onChange={handleSelectThread}
          >
            {messageThreads.map((thread) => (
              <MenuItem key={thread.slug} value={thread.slug}>
                {thread.product.name}
              </MenuItem>
            ))}
          </TextField>
          <MessagesList threadSlug={selectedThread} />
          {canSendMessage ? (
            <NewMessageForm onSuccess={onSuccess} threadSlug={selectedThread} />
          ) : null}
        </Stack>
      ) : null}
    </WhiteCard>
  );
};

export default Messaging;
